import { ILoadSchoolAssessmentsPayload } from './../../../store/actions/assessments-actions/school-assessments-actions';
import { ILoadStudentAssessmentsPayload } from './../../../store/actions/assessments-actions/student-assessments-actions';
import { TValidOtherSchoolTypes } from './../../typings/interfaces/student.interface';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, set, size } from 'lodash';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map as rxMap } from 'rxjs/operators';
import { IDocLog } from '../../../../app/shared/models/doc-log/doc-log';
import {
  IBulkUpdateSchoolAssessmentsPayload,
  IBulkUpdateStudentAssessmentsPayload,
  ICreateSchoolAssessmentPayload,
} from '../../../store';
import { ICreateSyncToolPayload, IDeleteSyncToolPayload } from '../../../store/actions/tools-actions';
import { SorterColumnDataType } from '../../constants/sorter-column-data-type.constant';
import { TClusterUserModalViewMode } from '../../modals/user-management/cluster-user/cluster-user-modals.config';
import { ICreateActivityParams } from '../../typings/interfaces/activity.interface';
import { IDataLoadParams } from '../../typings/interfaces/data-load.interface';
import { IMilestone } from '../../typings/interfaces/milestones.interface';
import { IStudentSupport } from '../../typings/interfaces/student-support.interface';
import { ISchoolSupport } from '../../typings/interfaces/support.interface';
import { ITool } from '../../typings/interfaces/tool.interface';
import { TValidMilestoneStatuses } from '../../typings/types/milestone.types';
import { ApiHelpers } from '../api-helpers';
import { GraphQLActivitiesHelperService } from '../graphql-helpers/activities/activities-queries.service';
import { GraphQLDataLoadsHelperService } from '../graphql-helpers/data-loads/data-loads-queries.service';
import { GraphQLSchoolAssessmentsHelperService } from '../graphql-helpers/school-assessments/school-assessments-queries.service';
import { GraphQLPartnerHelperService } from '../graphql-helpers/partner/partner-queries.service';
import { GraphQLStudentActivitiesHelperService } from '../graphql-helpers/student-activities/student-activities-queries.service';
import { GraphQLStudentAssessmentsHelperService } from '../graphql-helpers/student-assessments/student-assessments-queries.service';
import { GraphQLClusterUserHelperService } from '../graphql-helpers/user-management/cluster-user/cluster-user-queries.service';
import { PortalConfig } from '../portal-config';
import { IFlag } from './../../typings/interfaces/flags.interface';
import {
  IBulkUpdateStudentActivitiesParams,
  ICreateStudentActivityParams,
} from './../../typings/interfaces/student-activity.interface';
import { UtilitiesService } from './../utilities/utilities.service';
import { IApi, IApiStatus } from './api-service.interface';
import { IPartner } from '../../typings/interfaces/partner.interface';
import { GraphQlShelterClusterUserHelperService } from '../graphql-helpers/user-management/cluster-user/shelter-cluster-user-queries.service';

interface IStudentListData {
  schoolId: string;
  projection: object;
  joins: string[];
}

// Interface of the results return by backend for paginated results
export interface IPaginatedResult<T> {
  data: T[];
  items: {
    begin: number;
    end: number;
    limit: number;
    total: number;
  };
  pages: {
    current: number;
    hasNext: boolean;
    hasPrev: boolean;
    next: number;
    prev: number;
    total: number;
  };
}

export enum PATH_TYPE {
  COLLEGE = 'COLLEGE',
  CAREER = 'CAREER',
}

// TODO: shoud `get` methods be renamed to `fetch` to follow our norming convention
// around naming api requests? (CM).

/* istanbul ignore next: JJ */
@Injectable()
export class ApiService {
  private publicConfig;
  private NV_API_ORIGIN;
  public SINGLE_STUDENT_URL;
  public SINGLE_STUDENT_FROM_OTHER_SCHOOL_URL: string;
  public STUDENT_LIST_URL;
  public GRAPHQL_URL;
  public SCHOOL_URL;
  public FLAGS_URL;
  public USERS_URL;
  public DOC_LOGS_LIST_URL: string;
  public SUPPORTS_URL: string;
  public STUDENT_SUPPORTS_LIST_URL: string;
  public DASHBOARD_URL: string;
  public STUDENT_SUPPORTS_URL: string;
  public STUDENT_SUPPORTS_CREATE_URL: string;
  public STUDENT_SUPPORTS_BULK_CREATE_URL: string;
  public STUDENT_SUPPORTS_BULK_UPDATE_URL: string;
  public STUDENTS_BULK_URL: string;
  public MILESTONES_URL: string;
  public BULK_MILESTONES_URL: string;
  public STUDENT_PATH_URL: string;
  public PATHS_URL: string;
  public TOOLS_URL: string;
  public SYNC_URL: string;
  public API_STATUS_URL: string;
  constructor(
    private http: HttpClient,
    private portalConfig: PortalConfig,
    private apiHelpers: ApiHelpers,
    private utilitiesService: UtilitiesService,
    private graphQLDataLoadsHelperService: GraphQLDataLoadsHelperService,
    private graphQlActivitiesHelperService: GraphQLActivitiesHelperService,
    private graphQlStudentActivitiesHelperService: GraphQLStudentActivitiesHelperService,
    private graphQlSchoolAssessmentsHelperService: GraphQLSchoolAssessmentsHelperService,
    private graphQlStudentAssessmentsHelperService: GraphQLStudentAssessmentsHelperService,
    private graphQlClusterUserHelperService: GraphQLClusterUserHelperService,
    private GraphQLPartnerHelperService: GraphQLPartnerHelperService,
    private graphQlShelterClusterUserHelperService: GraphQlShelterClusterUserHelperService,
  ) {
    // Configs
    this.publicConfig = this.portalConfig.publicConfig;
    this.NV_API_ORIGIN = this.publicConfig.NV_API_ORIGIN;

    // Endpoints
    this.SINGLE_STUDENT_URL = this.NV_API_ORIGIN + '/v1/students/';
    this.SINGLE_STUDENT_FROM_OTHER_SCHOOL_URL = `${this.NV_API_ORIGIN}/v1/students/otherSchools/`;
    this.STUDENT_LIST_URL = this.NV_API_ORIGIN + '/v1/students/search';
    this.GRAPHQL_URL = this.NV_API_ORIGIN + '/graphql';
    this.SCHOOL_URL = this.NV_API_ORIGIN + '/v1/schools/';
    this.FLAGS_URL = `${this.NV_API_ORIGIN}/v1/flags/`;
    this.DOC_LOGS_LIST_URL = this.NV_API_ORIGIN + '/v1/docLogs/';
    this.SUPPORTS_URL = this.NV_API_ORIGIN + '/v1/supports/';
    this.USERS_URL = `${this.NV_API_ORIGIN}/v1/users/`;
    this.STUDENT_SUPPORTS_URL = this.NV_API_ORIGIN + '/v1/studentSupports/';
    this.STUDENT_SUPPORTS_LIST_URL = `${this.NV_API_ORIGIN}/v1/studentSupports/`;
    this.STUDENT_SUPPORTS_CREATE_URL = `${this.NV_API_ORIGIN}/v1/studentSupports`;
    // inconsistencies in the paths to the bulk create/update studentSupports handlers
    // is due to inconsistencies in the corresponding handlers path
    // values in the backend's plugins/routes.js file. (JYR)
    this.STUDENT_SUPPORTS_BULK_CREATE_URL = `${this.NV_API_ORIGIN}/v1/studentSupports/bulk/create`;
    this.STUDENT_SUPPORTS_BULK_UPDATE_URL = `${this.NV_API_ORIGIN}/v1/studentSupports/update/bulk`;
    this.DASHBOARD_URL = `${this.NV_API_ORIGIN}/v1/dashboards/`;
    this.STUDENTS_BULK_URL = `${this.NV_API_ORIGIN}/v1/students/bulk`;
    this.MILESTONES_URL = `${this.NV_API_ORIGIN}/v1/milestones/`;
    this.BULK_MILESTONES_URL = `${this.NV_API_ORIGIN}/v1/milestones/update/bulk`;
    this.STUDENT_PATH_URL = `${this.NV_API_ORIGIN}/v1/studentPaths/`;
    this.PATHS_URL = `${this.NV_API_ORIGIN}/v1/paths/`;
    this.TOOLS_URL = `${this.NV_API_ORIGIN}/v1/tools/`;
    this.SYNC_URL = `${this.NV_API_ORIGIN}/v1/listSyncer`;
    this.API_STATUS_URL = `${this.NV_API_ORIGIN}/v1/status`;
  }

  getAcademicListConstantsGraphQL(data: any): Observable<object> {
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getSchool(schoolId: string): Observable<object> {
    const url = `${this.SCHOOL_URL}${schoolId}`;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  getSingleStudent(studentId: string): Observable<object> {
    const url = `${this.SINGLE_STUDENT_URL}${studentId}`;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  getSingleStudentFromOtherSchool(studentId: string, otherSchoolType: TValidOtherSchoolTypes): Observable<object> {
    const url = `${this.SINGLE_STUDENT_FROM_OTHER_SCHOOL_URL}${otherSchoolType}/${studentId}`;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  getStudents(data: IStudentListData): Observable<object> {
    return this.http.post<any[]>(this.STUDENT_LIST_URL, data).pipe(
      rxMap((res: any) => this.apiHelpers.unzipData(res.data, data.projection)),
      catchError(err => throwError(err)),
    );
  }

  // change this name
  getStudentsGraphQL(data: any): Observable<any> {
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getClusterUserMadLib({ projection }) {
    const query = this.graphQlClusterUserHelperService.getClusterUserMadLibQuery({ projection });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getClusterUsers({ grouping, projection }): Observable<object> {
    const query = this.graphQlClusterUserHelperService.getClusterUsersQuery({ grouping, projection });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getClusterUserDetail({ userId }): Observable<object> {
    const query = this.graphQlClusterUserHelperService.getClusterUserDetailQuery({ userId });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getClusterUserSchools(userId): Observable<object> {
    const query = this.graphQlClusterUserHelperService.getClusterUserSchoolsQuery(userId);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  mutateClusterUser({ userPayload, columns }, mode: TClusterUserModalViewMode): Observable<object> {
    const query = this.graphQlClusterUserHelperService.getClusterUserMutationQuery({ userPayload, columns }, mode);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getActivities({ schoolId, where }): Observable<object> {
    const query = this.graphQlActivitiesHelperService.getActivitiesQuery(schoolId, where);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  patchActivity({ activityId, patch }): Observable<object> {
    const query = this.graphQlActivitiesHelperService.updateActivityQuery(activityId, patch);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  createActivity(activityParams: ICreateActivityParams): Observable<object> {
    const query = this.graphQlActivitiesHelperService.createActivityQuery(activityParams);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getStudentActivities({ schoolId, where }): Observable<object> {
    const query = this.graphQlStudentActivitiesHelperService.getStudentActivitiesQuery(schoolId, where);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  patchStudentActivity({ studentActivityId, patch }): Observable<object> {
    const query = this.graphQlStudentActivitiesHelperService.updateStudentActivityQuery(studentActivityId, patch);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  createStudentActivity(studentActivityParams: ICreateStudentActivityParams): Observable<object> {
    const query = this.graphQlStudentActivitiesHelperService.createStudentActivityQuery(studentActivityParams);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  bulkUpdateStudentActivities(payload: IBulkUpdateStudentActivitiesParams): Observable<any> {
    const query = this.graphQlStudentActivitiesHelperService.bulkUpdateStudentActivitiesQuery(payload);
    const data = {
      query,
      fetch: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getPartnerOrgs({ schoolId, where }): Observable<object> {
    const query = this.graphQlActivitiesHelperService.getPartnerOrgsQuery(schoolId, where);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  patchPartnerOrg({ partnerOrgId, patch }): Observable<object> {
    const query = this.graphQlActivitiesHelperService.updatePartnerOrgQuery(partnerOrgId, patch);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  createPartnerOrg(schoolId: string, partnerOrgParams: { name: string }): Observable<object> {
    const query = this.graphQlActivitiesHelperService.createPartnerOrgQuery(schoolId, partnerOrgParams);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  /** paths is a comma-separated list of student fields. This method returns
   * an object with student fields as keys and an array of distinct values
   * for those keys from the current user's caseload of students
   * example - paths: 'studentDetails.classOf,studentDetails.officialClass'
   * could return { id: null, classOf: ['2018', '2019'], officialClass: ['999', '201'] } (JE)
   */
  getMadlibOptions(schoolId: string, paths: string): Observable<object> {
    const payload = {
      distinct: paths,
      where: {
        schoolStatus: { $in: ['A'] },
        schoolId,
      },
    };
    return this.http.post<any[]>(this.STUDENT_LIST_URL, payload).pipe(catchError(err => throwError(err)));
  }

  getFlags(schoolId: string): Observable<object> {
    const url = `${this.FLAGS_URL}${schoolId}`;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  getUser(userId: string): Observable<object> {
    const url = `${this.USERS_URL}${userId}`;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  getSupport(supportId: string): Observable<ISchoolSupport> {
    const url = `${this.SUPPORTS_URL}${supportId}`;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err))) as Observable<ISchoolSupport>;
  }

  patchFlag(id: string, patch: Partial<IFlag>): Observable<IFlag> {
    const url = `${this.FLAGS_URL}${id}`;
    return this.http.patch(url, patch).pipe(catchError(err => throwError(err))) as Observable<IFlag>;
  }

  patchSingleStudent({ id, patch }): Observable<any> {
    const url = `${this.SINGLE_STUDENT_URL}${id}`;
    return this.http.patch(url, patch).pipe(catchError(err => throwError(err)));
  }
  patchStudents(patchData): Observable<HttpResponse<any>> {
    const url = `${this.STUDENTS_BULK_URL}`;
    let patches;
    const { _ids, path, newValue } = patchData;
    /**
     * this first condition only applies in cases where patchData.newValue
     * is an array of student(s), where each student contains:
     * studentId
     * dataType (may remove)
     * value: array of items that should be applied to the patchData.path. Each student
     * could have a different value. Dependent on what the existing value at the path
     * for each student contained.
     * At the time of this comment, this only applied to `pointPeople` path, where
     * a SINGLE point person is being updated. - JYR
     */
    if (newValue && newValue[0] && newValue[0].dataType === SorterColumnDataType.ARRAY) {
      patches = map(newValue, (updatedStudent: { _id: string; dataType: string; value: any[] }) => {
        const changes = set({}, path, updatedStudent.value);
        return {
          studentIds: [updatedStudent._id],
          patch: changes,
        };
      });
    } else {
      const changes = set({}, path, newValue);
      patches = [
        {
          studentIds: _ids,
          patch: changes,
        },
      ];
    }
    return this.http
      .patch<any[]>(url, { patches }, { observe: 'response' })
      .pipe(catchError((err: any) => throwError(err)));
  }

  getStudentSupports(schoolId: string, queries?: { where?: any }, projection?): Observable<object> {
    const queryParamsExist = !!size(queries);
    let url = `${this.STUDENT_SUPPORTS_LIST_URL}?schoolId=${schoolId}`;

    if (queryParamsExist) url = this.utilitiesService.addQueriesToUrl(url, queries);

    if (projection) {
      const projectionQuery = JSON.stringify(projection);
      url += `&projection=${projectionQuery}`;
    }

    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  createStudentSupport(data: IStudentSupport, schoolId: string): Observable<IStudentSupport> {
    const url = `${this.STUDENT_SUPPORTS_CREATE_URL}?schoolId=${schoolId}`;
    return this.http.post<IStudentSupport>(url, data).pipe(catchError(err => throwError(err)));
  }

  createStudentSupports(data: IApi['CreateStudentSupportsPayload']): Observable<HttpResponse<any>> {
    const url = this.STUDENT_SUPPORTS_BULK_CREATE_URL;
    return this.http.post<object>(url, data, { observe: 'response' }).pipe(catchError(err => throwError(err)));
  }

  patchStudentSupport(id: string, patch: Partial<IStudentSupport>): Observable<IStudentSupport> {
    const url = `${this.STUDENT_SUPPORTS_URL}${id}`;
    return this.http.patch(url, patch).pipe(catchError(err => throwError(err))) as Observable<IStudentSupport>;
  }

  patchStudentSupports(data: IApi['PatchStudentSupportsPayload']): Observable<HttpResponse<any>> {
    const { studentSupportIds, status } = data;
    const payload = {
      patches: [
        {
          studentSupportIds,
          patch: { status },
        },
      ],
    };
    const url = this.STUDENT_SUPPORTS_BULK_UPDATE_URL;
    return this.http.patch(url, payload, { observe: 'response' }).pipe(catchError(err => throwError(err)));
  }

  getGraph(schoolId: string, graphName: string, graphQuery): Observable<object> {
    let url;
    if (graphQuery) url = this.DASHBOARD_URL + schoolId + '/' + graphName + '/?' + graphQuery;
    else url = this.DASHBOARD_URL + schoolId + '/' + graphName;
    return this.http.get<object>(url).pipe(catchError(err => throwError(err)));
  }

  public getPaginatedDocLogs(
    schoolId: string,
    queries?: {
      page?: number; // api default is 1
      limit?: number; // api default is 50
      sort?: string; // api default is '-createdAt'
      docId?: string;
      collection?: string;
      loggedBefore?: string;
      loggedAfter?: string;
      where?: string;
      displayHistory?: boolean;
    },
  ): Observable<IPaginatedResult<IDocLog>> {
    const queryParamsExist = !!size(queries);
    let url = `${this.DOC_LOGS_LIST_URL}${schoolId}`;

    if (queryParamsExist) url = this.utilitiesService.addQueriesToUrl(url, queries);

    return this.http.get<IPaginatedResult<IDocLog>>(url).pipe(catchError(err => throwError(err)));
  }

  public getPaginatedSupports(
    schoolId: string,
    queries?: {
      projection?: string;
      where: string;
    },
  ): Observable<IPaginatedResult<ISchoolSupport>> {
    const queryParamsExist = !!size(queries);
    let url = this.SUPPORTS_URL + `?schoolId=${schoolId}`;

    if (queryParamsExist) if (queryParamsExist) url = this.utilitiesService.addQueriesToUrl(url, queries);

    return this.http.get<IPaginatedResult<ISchoolSupport>>(url).pipe(catchError(err => throwError(err)));
  }

  getMilestones(payload: {
    schoolId: string;
    projection?: any;
    queries?: any;
    studentId?: string;
  }): Observable<IPaginatedResult<IMilestone>> {
    const { schoolId, projection, queries, studentId } = payload;
    let url = `${this.MILESTONES_URL}${schoolId}`;

    if (studentId) {
      url += '?studentId=' + studentId;
    } else {
      const queryParamsExist = !!size(queries);
      if (queryParamsExist) url = this.utilitiesService.addQueriesToUrl(url, queries);
      if (projection) {
        const projectionQuery = JSON.stringify(projection);
        url += `?projection=${projectionQuery}`;
      }
    }

    return this.http.get<IPaginatedResult<IMilestone>>(url).pipe(catchError(err => throwError(err)));
  }

  getStudentPaths(payload: {
    schoolId: string;
    projection?: any;
    queries?: any;
    studentId?: string;
  }): Observable<Array<object>> {
    const { schoolId, projection, queries, studentId } = payload;
    let url = `${this.STUDENT_PATH_URL}${schoolId}`;

    if (studentId) {
      url += '?studentId=' + studentId;
    } else {
      const queryParamsExist = !!size(queries);
      if (queryParamsExist) url = this.utilitiesService.addQueriesToUrl(url, queries);
      if (projection) {
        const projectionQuery = JSON.stringify(projection);
        url += `?projection=${projectionQuery}`;
      }
    }

    return this.http.get<Array<object>>(url).pipe(catchError(err => throwError(err)));
  }

  createStudentPath(schoolId: string, studentId: string, pathId: string): Observable<object> {
    const url = this.STUDENT_PATH_URL + schoolId + '/' + studentId + '/' + pathId;

    return this.http.post<object>(url, {}).pipe(catchError(err => throwError(err)));
  }

  bulkCreateStudentPath(payload: {
    schoolId: string;
    studentIds: string[];
    pathId: string;
  }): Observable<HttpResponse<any>> {
    const url = this.STUDENT_PATH_URL + 'create/bulk';
    return this.http.post<object>(url, payload, { observe: 'response' }).pipe(catchError(err => throwError(err)));
  }

  patchStudentPaths(data: IApi['PatchStudentPathsPayload']): Observable<HttpResponse<any>> {
    const { studentPathIds, status, studentIds, pathId, schoolId, downgradeMap } = data;
    // if downgradeMap exists, these studentPaths need to be downgraded BEFORE the new status is patched (JE)

    const payload = {
      downgradeMap,
      patches: [
        {
          studentPathIds,
          patch: { status },
          studentIds,
          pathId,
          schoolId,
        },
      ],
    };
    const url = `${this.STUDENT_PATH_URL}update/bulk`;
    return this.http.patch(url, payload, { observe: 'response' }).pipe(catchError(err => throwError(err)));
  }

  updateStudentPath(studentPathId: string, status: string): Observable<object> {
    const url = this.STUDENT_PATH_URL + studentPathId;
    const payload = {
      status,
    };

    return this.http.patch<object>(url, payload).pipe(catchError(err => throwError(err)));
  }

  createPath(schoolId: string, pathType: PATH_TYPE, payload): Observable<object> {
    const url = this.PATHS_URL + schoolId + '/' + pathType;

    return this.http.post<object>(url, payload).pipe(catchError(err => throwError(err)));
  }

  getPaths(schoolId: string, pathType: PATH_TYPE): Observable<Array<object>> {
    const url = this.PATHS_URL + schoolId + '/' + pathType;

    return this.http.get<Array<object>>(url).pipe(catchError(err => throwError(err)));
  }

  patchMilestone(docId: string, patch: Partial<IMilestone>) {
    const url = `${this.MILESTONES_URL}${docId}`;

    return this.http.patch(url, patch).pipe(catchError(err => throwError(err)));
  }

  bulkPatchMilestones(
    milestoneIds: string[],
    status: TValidMilestoneStatuses,
    whereParams?: { studentIds?: string[]; key?: string; schoolId?: string },
  ): Observable<HttpResponse<any>> {
    let url = `${this.BULK_MILESTONES_URL}`;
    const payload = { patches: [{ milestoneIds, patch: { status } }], where: null };
    if (whereParams) {
      let where: { studentId?: any; key?: string; schoolId?: string } = {};
      const { studentIds, key, schoolId } = whereParams;
      if (studentIds) where.studentId = { $in: studentIds };
      if (key) where.key = key;
      if (schoolId) where.schoolId = schoolId;
      payload.where = where;
    }
    return this.http.patch(url, payload, { observe: 'response' }).pipe(catchError(err => throwError(err)));
  }

  getTools(schoolId: string): Observable<Array<object>> {
    const url = `${this.TOOLS_URL}?schoolId=${schoolId}`;
    return this.http.get<ITool[]>(url).pipe(catchError(err => throwError(err)));
  }

  createSyncTool(payload: ICreateSyncToolPayload): Observable<any> {
    const url = `${this.SYNC_URL}/create`;
    return this.http.post<ITool>(url, payload).pipe(catchError(err => throwError(err)));
  }

  deleteSyncTool(payload: IDeleteSyncToolPayload): Observable<any> {
    const url = `${this.SYNC_URL}/delete`;
    return this.http.post(url, payload).pipe(catchError(err => throwError(err)));
  }

  getSchoolAssessments(payload: ILoadSchoolAssessmentsPayload): Observable<any> {
    const query = this.graphQlSchoolAssessmentsHelperService.getSchoolAssessmentsQuery(payload);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  createSchoolAssessments(params: ICreateSchoolAssessmentPayload): Observable<any> {
    const query = this.graphQlSchoolAssessmentsHelperService.bulkCreateSchoolAssessmentsQuery(params);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  patchSchoolAssessments(params: IBulkUpdateSchoolAssessmentsPayload): Observable<any> {
    const query = this.graphQlSchoolAssessmentsHelperService.bulkUpdateSchoolAssessmentsQuery(params);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getStudentAssessments(params: ILoadStudentAssessmentsPayload): Observable<any> {
    const query = this.graphQlStudentAssessmentsHelperService.getStudentAssessmentsQuery(params);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  patchStudentAssessments(params: IBulkUpdateStudentAssessmentsPayload): Observable<any> {
    const query = this.graphQlStudentAssessmentsHelperService.bulkUpdateStudentAssessmentsQuery(params);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getDataLoads(schoolId: string, where?: any): Observable<any> {
    const query = this.graphQLDataLoadsHelperService.getDataLoadsQuery(schoolId, where);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  createDataLoad(params: IDataLoadParams): Observable<any> {
    const query = this.graphQLDataLoadsHelperService.createDataLoad(params);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  // fetch a single projected or full partner doc
  getPartner({ params, projections }: IApi['GetPartnerPayload']): Observable<IPartner> {
    const query = this.GraphQLPartnerHelperService.getPartnerQuery({ params, projections });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<IApi['GetPartnerRes']>(this.GRAPHQL_URL, data).pipe(
      rxMap((res: IApi['GetPartnerRes']) => {
        return res.data.Partner;
      }),
      catchError(err => throwError(err)),
    );
  }

  // fetch a list of projected or full partner docs
  getPartners({ params, projections }: IApi['GetPartnersPayload']): Observable<IPartner[]> {
    const query = this.GraphQLPartnerHelperService.getPartnersQuery({ params, projections });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<IApi['GetPartnersRes']>(this.GRAPHQL_URL, data).pipe(
      rxMap((res: IApi['GetPartnersRes']) => {
        return res.data.Partners;
      }),
      catchError(err => throwError(err)),
    );
  }

  // fetch list of shelter cluster users
  getShelterClusterUsers({ projection }): Observable<any> {
    const query = this.graphQlShelterClusterUserHelperService.getShelterClusterUsersQuery({ projection });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<IApi['GetShelterClusterUsersRes']>(this.GRAPHQL_URL, data).pipe(
      rxMap((res: IApi['GetShelterClusterUsersRes']) => {
        return res.data.ShelterClusterUserGrouping;
      }),
      catchError(err => throwError(err)),
    );
  }

  // populate the add/edit user modal
  getShelterClusterUserDetail({ userId }): Observable<object> {
    const query = this.graphQlShelterClusterUserHelperService.getShelterClusterUserDetailQuery({ userId });
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  // populate shelter cluster users portfolio modal
  getShelterClusterUserShelters(userId): Observable<object> {
    const query = this.graphQlShelterClusterUserHelperService.getShelterClusterUserSheltersQuery(userId);
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  mutateShelterClusterUser({ userPayload, columns }, mode: TClusterUserModalViewMode): Observable<object> {
    const query = this.graphQlShelterClusterUserHelperService.getShelterClusterUserMutationQuery(
      { userPayload, columns },
      mode,
    );
    const data = {
      query,
      fetchPolicy: 'no-cache',
    };
    return this.http.post<any[]>(this.GRAPHQL_URL, data).pipe(catchError(err => throwError(err)));
  }

  getApiStatus(): Observable<IApiStatus> {
    return this.http.get<IApiStatus>(this.API_STATUS_URL).pipe(catchError(err => throwError(err)));
  }
}
